import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import "./index.css";
import {
  Header,
  Hero,
  Features,
  VenueCard,
  VenuesInfo,
  EventsInfo,
  InteractiveCarousel,
  Testimonial,
  About,
  CookieConsent,
  VenueRegistration,
  PrivacyPolicy,
  TermsOfService,
  PageWrapper,
  SearchPageWrapper,
  ErrorBoundary,
  SearchResults,
  VenueDetails,
  BlogList,
  BlogPost,
  AIChatButton,
  NewsletterPopup
} from "./components";
import SearchBar from "./components/SearchBar";
import BookingPage from "./components/BookingPage/BookingPage";
import VenueDashboardWrapper from "./components/VenueDashboardWrapper/VenueDashboardWrapper";
import VenueDashboardDemo from "./components/VenueDashboardDemo/VenueDashboardDemo";
import Profile from "./components/Profile/Profile";
import BookingDetails from "./components/Profile/BookingDetails";
import DashboardLogin from "./pages/DashboardLogin/DashboardLogin";
import StripeConnectCallbackPage from "./pages/StripeConnectCallback/StripeConnectCallbackPage";
import GuestBookingPaymentPage from "./pages/GuestBookingPayment/GuestBookingPaymentPage";
import BookingFeedbackPage from "./pages/BookingFeedback/BookingFeedbackPage";
import VerifyEmail from "./pages/VerifyEmail/VerifyEmail";
import Landing2 from "./pages/Landing2/Landing2";
import Messages from "./pages/Messages/Messages";
import ManualBooking from "./pages/ManualBooking/ManualBooking";
import ManualBookingsAdmin from "./pages/ManualBookingsAdmin/ManualBookingsAdmin";
import AdminOrganizers from "./pages/AdminOrganizers/AdminOrganizers";
import AdminVenuePayments from "./pages/AdminVenuePayments/AdminVenuePayments";
import AdminVenuePricing from "./pages/AdminVenuePricing/AdminVenuePricing";
import AdminHub from "./pages/Admin/AdminHub";
import AdminCatalogAddonTags from "./pages/AdminCatalogAddonTags/AdminCatalogAddonTags";
import VenueLists from "./pages/VenueLists/VenueLists";
import VenueListDetail from "./pages/VenueLists/VenueListDetail";
import VenueListInviteAccept from "./pages/VenueLists/VenueListInviteAccept";
import Landing2Footer from "./components/Landing2Footer/Landing2Footer";
import ScrollToTop from "./components/ScrollToTop/ScrollToTop";
import AuthGate from "./components/AuthGate/AuthGate";
import { isAuthGateDiscoveryEnforced } from "./config/authGate";
import AuthRequiredModalTrigger from "./components/AuthGate/AuthRequiredModalTrigger";
import BookingAccessMismatchModal from "./components/AuthGate/BookingAccessMismatchModal";
import AdminOnlyRoute from "./components/AdminOnlyRoute/AdminOnlyRoute";
import { AuthRequiredProvider } from "./contexts/AuthRequiredContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";

// Home page component - keeping original structure
const HomePage = () => {
  return (
    <section>
      <section className="landing-bg">
        <div className="relative content-width mx-auto container-padding ">
          <Header />
          <Hero />
          <SearchBar />
        </div>
      </section>
      <section className="pt-24">
          <Features />
          <VenueCard />
          <InteractiveCarousel />
          <VenueRegistration />
          <Testimonial />
      </section>
      <Landing2Footer />
      
      <CookieConsent />
      <NewsletterPopup />
      {/* <div className="landing-wrapper container-padding">
        <div className="" style={{ minHeight: '876px' }}>
          <Header />
          <Hero />
        </div>
        <SearchBar />
      </div>
      <Features />
      <VenueCard />
      <InteractiveCarousel />
      <VenueRegistration />
      <Testimonial />
      <Footer />
      <CookieConsent /> */}
    </section>
  );
};

// About page component - includes Landing2 header and Footer
const AboutPage = () => {
  return (
    <PageWrapper useNewFooter useLanding2Header>
      <About />
    </PageWrapper>
  );
};

// Privacy Policy page
const PrivacyPage = () => {
  return (
    <PageWrapper useNewFooter useLanding2Header>
      <PrivacyPolicy />
    </PageWrapper>
  );
};

// Terms of Service page
const TermsPage = () => {
  return (
    <PageWrapper useNewFooter useLanding2Header>
      <TermsOfService />
    </PageWrapper>
  );
};



// Search Results Wrapper - manages search state and passes to both Header and SearchResults
const SearchResultsWrapper = () => {
  return (
    <SearchPageWrapper useNewFooter>
      <SearchResults />
    </SearchPageWrapper>
  );
};

// Search Results page component - standalone
const SearchResultsPage = () => {
  return <SearchResultsWrapper />;
};

// Events info page - includes Header and new Footer
const EventsInfoPage = () => {
  return (
    <PageWrapper useNewFooter>
      <EventsInfo />
    </PageWrapper>
  );
};

// Venue Details Wrapper — header without search bar
const VenueDetailsWrapper = () => {
  return (
    <PageWrapper useNewFooter useLanding2Header>
      <VenueDetails />
    </PageWrapper>
  );
};

// Booking Page Wrapper - uses the same site header as the venue and marketing pages
const BookingPageWrapper = () => {
  return (
    <PageWrapper useNewFooter useLanding2Header>
      <BookingPage />
    </PageWrapper>
  );
};

// Venues info page - includes Header and new Footer
const VenuesInfoPage = () => {
  return (
    <PageWrapper useNewFooter>
      <VenuesInfo />
    </PageWrapper>
  );
};

const BlogListPage = () => {
  return (
    <PageWrapper useNewFooter useLanding2Header>
      <BlogList />
    </PageWrapper>
  );
};

const BlogPostPage = () => {
  return (
    <PageWrapper useNewFooter useLanding2Header>
      <BlogPost />
    </PageWrapper>
  );
};

// Global AI Chat Button Component - needs auth context
const GlobalAIChatButton = () => {
  const { currentUser, userProfile } = useAuth();
  
  const userContext = {
    userId: currentUser?.uid || null,
    userEmail: currentUser?.email || null,
    userType: userProfile?.user_type || 'customer',
    venueId: userProfile?.owned_venues?.[0]?.id || null,
    timestamp: new Date().toISOString()
  };

  return <AIChatButton userContext={userContext} />;
};

/** Venues marketing is Landing2 at /onboard. Organisers intake stays at /. */
const RootRoute = () => {
  const [searchParams] = useSearchParams();
  if (searchParams.get("persona") === "venues") {
    return <Navigate to="/onboard?persona=venues" replace />;
  }
  return <ManualBooking />;
};

const OnboardRoute = () => {
  const [searchParams] = useSearchParams();
  if (searchParams.get("persona") === "events") {
    return <Navigate to="/" replace />;
  }
  return <Landing2 />;
};

// Helper function to check if on venue dashboard subdomain
const isVenueDashSubdomain = () => {
  const hostname = window.location.hostname;
  return hostname === 'venues.occasia.io' || 
         hostname.startsWith('venues.') ||
         hostname.includes('venues.occasia.io');
};

// App Routes Component - needs to be inside AuthProvider
const discoveryAuthEnforced = isAuthGateDiscoveryEnforced();

const AppRoutes = () => {
  const isVenueDash = isVenueDashSubdomain();
  
  return (
    <Router>
      <AuthRequiredProvider>
        <ScrollToTop />
        <AuthRequiredModalTrigger />
        <BookingAccessMismatchModal />
        <Routes>
        {/* Redirect subdomain root to login */}
        {isVenueDash ? (
          <Route path="/" element={<Navigate to="/dashboard-login" replace />} />
        ) : (
          <Route path="/" element={<RootRoute />} />
        )}
        <Route path="/about" element={<AboutPage />} />
        <Route path="/blog" element={<BlogListPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/search" element={<AuthGate enforce={discoveryAuthEnforced}><SearchResultsPage /></AuthGate>} />
        <Route path="/search-results" element={<AuthGate enforce={discoveryAuthEnforced}><SearchResultsPage /></AuthGate>} />
        <Route path="/venue/:venueId" element={<AuthGate mode="blur" allowNonPartnerVenueById enforce={discoveryAuthEnforced}><VenueDetailsWrapper /></AuthGate>} />
        {/* Booking form collects name/email/phone — no login required to request a booking */}
        <Route path="/booking/:venueId" element={<BookingPageWrapper />} />
        <Route path="/onboard" element={<OnboardRoute />} />
        <Route path="/welcome" element={<Navigate to="/onboard?persona=venues" replace />} />
        <Route path="/manual-booking" element={<Navigate to="/onboard?persona=venues" replace />} />
        <Route
          path="/admin"
          element={
            <AdminOnlyRoute>
              <AdminHub />
            </AdminOnlyRoute>
          }
        />
        <Route
          path="/admin/venues/payments"
          element={
            <AdminOnlyRoute>
              <AdminVenuePayments />
            </AdminOnlyRoute>
          }
        />
        <Route
          path="/admin/venues/pricing"
          element={
            <AdminOnlyRoute>
              <AdminVenuePricing />
            </AdminOnlyRoute>
          }
        />
        <Route
          path="/admin/organizers"
          element={
            <AdminOnlyRoute>
              <AdminOrganizers />
            </AdminOnlyRoute>
          }
        />
        <Route
          path="/admin/manual-bookings"
          element={
            <AdminOnlyRoute>
              <ManualBookingsAdmin />
            </AdminOnlyRoute>
          }
        />
        <Route
          path="/admin/catalog-addon-tags"
          element={
            <AdminOnlyRoute>
              <AdminCatalogAddonTags />
            </AdminOnlyRoute>
          }
        />
        <Route path="/venues-info" element={<VenuesInfoPage />} />
        <Route path="/events-info" element={<EventsInfoPage />} />
        <Route path="/venue-dashboard" element={<VenueDashboardWrapper />} />
        <Route path="/venue-dashboard-demo" element={<VenueDashboardDemo />} />
        <Route path="/stripe/connect/callback" element={<StripeConnectCallbackPage />} />
        <Route path="/pay/booking/:bookingId" element={<GuestBookingPaymentPage />} />
        <Route path="/feedback/booking/:bookingId" element={<BookingFeedbackPage />} />
        <Route path="/profile" element={<AuthGate><Profile /></AuthGate>} />
        <Route path="/profile/bookings/:bookingId" element={<AuthGate><BookingDetails /></AuthGate>} />
        <Route path="/venue-lists" element={<AuthGate><VenueLists /></AuthGate>} />
        <Route path="/venue-lists/accept" element={<VenueListInviteAccept />} />
        <Route path="/venue-lists/accept/*" element={<VenueListInviteAccept />} />
        <Route path="/venue-lists/:listId" element={<AuthGate><VenueListDetail /></AuthGate>} />
        <Route path="/messages" element={<AuthGate><Messages /></AuthGate>} />
        <Route path="/dashboard-login" element={<DashboardLogin />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/landing2" element={<HomePage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        {/* Global AI Chat Button - appears on all pages (must be inside Router for useNavigate) */}
        <GlobalAIChatButton />
      </AuthRequiredProvider>
    </Router>
  );
};

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </ErrorBoundary>
  );
}
